import React, { forwardRef, memo, useImperativeHandle } from 'react';

import { useDisclosure } from '@chakra-ui/hooks';

import { bulkCreatePickup } from 'api/pickup';
import { useMutation, useQueryClient } from 'react-query';
import { useCollectionPoints } from 'hooks/useCollectionPoints';
import { useSites } from 'hooks';

import { successToast } from 'utils/successToast';
import { toApiPayloadDateFormat } from 'utils/dateUtils';

import { useAppSelector } from 'store/hooks';
import { locationSelector } from 'store/slices/washhub';

import {
  AddPickupFormValues,
  AddPickupModalProps,
  RefType,
} from './AddPickupModal.props';
import { SiteTypeEnum } from 'types/site';

import AddPickupModalView from './AddPickupModal.view';

const Container = forwardRef<RefType, AddPickupModalProps>((props, ref) => {
  const { selectedDay } = props;
  const queryClient = useQueryClient();

  const selectedWashHub = useAppSelector(locationSelector);
  const washhubId = selectedWashHub?._id || '';

  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    data: collectionPoints = [],
    isLoading: isLoadingPoints,
  } = useCollectionPoints();
  const { data: sites } = useSites();

  useImperativeHandle(
    ref,
    () => ({
      onOpen,
    }),
    [onOpen],
  );

  const { mutateAsync, isLoading: isCreating } = useMutation(bulkCreatePickup, {
    onSuccess: (response) => {
      const count = Array.isArray(response?.item)
        ? response.item.length
        : Array.isArray(response)
        ? response.length
        : 1;
      successToast(
        count === 1
          ? 'Pickup Created Successfully'
          : `${count} Pickups Created Successfully`,
      );

      queryClient.invalidateQueries(`fetch-pickups-${washhubId}`);
      queryClient.invalidateQueries(`fetch-pickups-status-count-${washhubId}`);
      queryClient.invalidateQueries(`fetch-orders`);
      queryClient.invalidateQueries(['fetch-pickups']);

      onClose();
    },
  });

  const onCreate = async (values: AddPickupFormValues) => {
    const site = sites?.find((s) => s._id === values.site);
    const cp = collectionPoints?.find(
      (c) => (c.site?._id ?? c.site) === values.site,
    );

    if (!cp) {
      return;
    }

    const isThirdParty = site?.siteType === SiteTypeEnum.THIRD_PARTY_WASH;

    const items = values.items.map((item) => ({
      date: toApiPayloadDateFormat(item.date as any),
      eventName: item.eventName || undefined,
      notes: item.notes || undefined,
      isFinalPickup: isThirdParty ? false : !!item.isFinalPickup,
    }));

    return mutateAsync({
      collectionPoint: cp._id,
      items,
    });
  };

  return (
    <AddPickupModalView
      isOpen={isOpen}
      onClose={onClose}
      sites={sites}
      collectionPoints={collectionPoints}
      isLoadingPoints={isLoadingPoints}
      isCreating={isCreating}
      selectedDay={selectedDay}
      onCreate={onCreate}
    />
  );
});

const AddPickupModal = memo(Container);

export default AddPickupModal;
